import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  OrganizationUserGroupDto,
  UserGroupChangeDto,
  UserGroupCreateDto,
  UserGroupMemberDto,
  UserGroupMembersChangeDto,
} from '../entities/user-group-entities/organization-user-group-dto';
import {
  OrganizationDirectoryDto,
  UserGroupDirectoryPermissionDto,
  UserGroupDirectoryPermissionsChangeDto,
} from '../entities/user-group-entities/user-group-directory-permission-dto';
import { UserGroupVisibleOrganizationDto, UserGroupVisibleOrganizationsChangeDto } from '../entities/user-group-entities/user-group-visible-organization-dto';
import { ParentApiService } from './parent-api-service';

@Injectable({ providedIn: 'root' })
export class UserGroupsApiService extends ParentApiService {
  private readonly baseUrl = `${environment.webApiAddress}/api/my-organization/user-groups`;

  constructor(private readonly http: HttpClient) {
    super();
  }

  getGroups(stayOnPageIfError?: boolean): Observable<OrganizationUserGroupDto[]> {
    return this.http.get<OrganizationUserGroupDto[]>(this.baseUrl, { headers: this.headersFor(stayOnPageIfError) });
  }

  createGroup(dto: UserGroupCreateDto, stayOnPageIfError?: boolean): Observable<string> {
    return this.http.post<string>(this.baseUrl, dto, { headers: this.headersFor(stayOnPageIfError) });
  }

  updateGroup(id: string, dto: UserGroupChangeDto, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}`, dto, { headers: this.headersFor(stayOnPageIfError) });
  }

  deleteGroup(id: string, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`, { headers: this.headersFor(stayOnPageIfError) });
  }

  getMembers(id: string, stayOnPageIfError?: boolean): Observable<UserGroupMemberDto[]> {
    return this.http.get<UserGroupMemberDto[]>(`${this.baseUrl}/${id}/members`, { headers: this.headersFor(stayOnPageIfError) });
  }

  getUsersWithGroups(stayOnPageIfError?: boolean): Observable<UserGroupMemberDto[]> {
    return this.http.get<UserGroupMemberDto[]>(`${this.baseUrl}/users`, { headers: this.headersFor(stayOnPageIfError) });
  }

  addMembers(id: string, dto: UserGroupMembersChangeDto, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/members/add`, dto, { headers: this.headersFor(stayOnPageIfError) });
  }

  removeMembers(id: string, dto: UserGroupMembersChangeDto, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}/members/remove`, dto, { headers: this.headersFor(stayOnPageIfError) });
  }

  getDirectories(stayOnPageIfError?: boolean): Observable<OrganizationDirectoryDto[]> {
    return this.http.get<OrganizationDirectoryDto[]>(`${this.baseUrl}/directories`, { headers: this.headersFor(stayOnPageIfError) });
  }

  getDirectoryPermissions(directoryIds?: number[], stayOnPageIfError?: boolean): Observable<UserGroupDirectoryPermissionDto[]> {
    const params = new HttpParams().appendAll({ 'directory-ids': directoryIds ?? [] });
    return this.http.get<UserGroupDirectoryPermissionDto[]>(`${this.baseUrl}/directory-permissions`, { headers: this.headersFor(stayOnPageIfError), params });
  }

  setDirectoryPermissions(dto: UserGroupDirectoryPermissionsChangeDto, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/directory-permissions`, dto, { headers: this.headersFor(stayOnPageIfError) });
  }

  getUncoveredDirectories(stayOnPageIfError?: boolean): Observable<number[]> {
    return this.http.get<number[]>(`${this.baseUrl}/uncovered-directories`, { headers: this.headersFor(stayOnPageIfError) });
  }

  getVisibleOrganizations(stayOnPageIfError?: boolean): Observable<UserGroupVisibleOrganizationDto[]> {
    return this.http.get<UserGroupVisibleOrganizationDto[]>(`${this.baseUrl}/visible-organizations`, { headers: this.headersFor(stayOnPageIfError) });
  }

  setVisibleOrganizations(dto: UserGroupVisibleOrganizationsChangeDto, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/visible-organizations`, dto, { headers: this.headersFor(stayOnPageIfError) });
  }

  getUncoveredOrganizations(stayOnPageIfError?: boolean): Observable<number[]> {
    return this.http.get<number[]>(`${this.baseUrl}/uncovered-organizations`, { headers: this.headersFor(stayOnPageIfError) });
  }
}
